import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Fehler, Laden, Leer } from '../components/common/States'
import {
  useDatenwaechter,
  useDatenwaechterFreigeben,
  useJobs,
  useTriggerJob,
} from '../hooks/useQualyz'
import { fehlerText } from '../lib/api'
import { formatDatum, formatDezimal } from '../lib/lenses'
import type { Job } from '../services/qualyz'

/**
 * Importe ohne Terminal (Anforderung Kim, 29.08.2026: "ohne KI-Hilfe").
 *
 * Liest den Job-Katalog aus /api/admin/jobs. Die Trennung "ohne Abo" /
 * "mit Abo" ist der eigentliche Zweck: Waehrend das FMP-Abo pausiert, muessen
 * Kurse, Bereinigung und Rueckvergleich weiter startbar bleiben.
 */
export default function ImportsPage() {
  const { isAdmin, isLoading } = useAuth()
  if (isLoading) return null
  if (!isAdmin) {
    return (
      <div className="q-page">
        <section className="q-card">
          <Leer text="Dieser Bereich ist Administratoren vorbehalten." />
        </section>
      </div>
    )
  }
  return <Katalog />
}

/**
 * Datenwaechter: Steht die Anzeige auf dem letzten geprueften Stand?
 *
 * Gesperrt wird, wenn die Pruefliste nach einem Import abweicht
 * (backend/datenwaechter.py). Die Listen bleiben dann stehen; erst die
 * Freigabe rechnet sie neu.
 */
/**
 * Bestand gegen die SEC (jobs/bestandspruefung.py, 31.10.2026): Gehoert das
 * Kuerzel zur Firma, sind alle Perioden da, stimmen die Werte? Eine Abweichung
 * heisst nicht "falsch" -- oft ist es Abgrenzung oder ein Fehler in den Daten
 * der SEC. Wichtig ist, ob die Quoten sich zwischen zwei Laeufen bewegen.
 */
function BestandKarte() {
  const stand = useDatenwaechter()
  if (stand.isPending || stand.isError || !stand.data) return null
  const b = stand.data.bestand
  return (
    <section className="q-card q-waechter">
      <div className="q-card-head">
        <h3 className="q-card-title">Bestand gegen die SEC</h3>
        {b && <span className="q-job-meta">Geschäftsjahr {b.jahr} · {formatDatum(b.geprueft_am.slice(0, 10))}</span>}
      </div>
      {!b ? (
        <p className="q-job-text">Noch nicht gelaufen. Start über „Bestand gegen die SEC prüfen“ unten.</p>
      ) : (
        <>
          <p className="q-job-text">
            {b.firmen} Firmen. Jahre {(b.perioden.jahre ?? 0) - (b.perioden.jahre_fehlen ?? 0)} von {b.perioden.jahre ?? 0}, Quartale{' '}
            {(b.perioden.quartale ?? 0) - (b.perioden.quartale_fehlen ?? 0)} von {b.perioden.quartale ?? 0} da;{' '}
            {b.perioden.firmen_mit_luecken} Firmen mit Lücken, {b.perioden.ohne_zeile} ganz ohne Zahlen.
          </p>
          <ul className="q-waechter-liste">
            {Object.entries(b.werte).map(([name, w]) => (
              <li key={name}>
                <strong>{name}</strong> {formatDezimal(w.gleich_pct, 1)} % gleich, {formatDezimal(w.nah_pct, 1)} % unter 5 %
                {' '}({w.verglichen} Titel, {w.abweichend} abweichend{w.einheit ? `, ${w.einheit} Faktor 1.000 oder mehr` : ''})
              </li>
            ))}
          </ul>
          {b.zuordnung.andere_firma.length > 0 && (
            <>
              <p className="q-job-text">Name passt nicht und keine Zahl bestätigt die Firma, bitte nachsehen:</p>
              <ul className="q-waechter-liste">
                {b.zuordnung.andere_firma.slice(0, 15).map((a) => (
                  <li key={a.symbol}>
                    <strong>{a.symbol}</strong> {a.unser} · bei der SEC: {a.sec}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  )
}

function Datenwaechter() {
  const stand = useDatenwaechter()
  const freigeben = useDatenwaechterFreigeben()
  if (stand.isPending || stand.isError || !stand.data) return null

  const { gesperrt, sperre, pruefliste } = stand.data
  const fehler = (sperre?.befunde ?? pruefliste?.befunde ?? []).filter((b) => b.schwere === 'fehler')

  return (
    <section className={`q-card q-waechter${gesperrt ? ' is-gesperrt' : ''}`}>
      <div className="q-card-head">
        <h3 className="q-card-title">Datenwächter</h3>
        <span className={`q-badge ${gesperrt ? 'q-badge--warn' : 'q-badge--ok'}`}>
          {gesperrt ? 'Anzeige gesperrt' : 'frei'}
        </span>
      </div>

      {gesperrt ? (
        <>
          <p className="q-job-text">
            {sperre?.grund}
            {sperre?.quelle ? ` · ausgelöst vom ${sperre.quelle}` : ''}
            {sperre?.seit ? ` · seit ${formatDatum(sperre.seit.slice(0, 10))}` : ''}
          </p>
          <p className="q-job-text">
            Top-Ideen und Screener zeigen den letzten geprüften Stand. Die Einzelansicht
            rechnet weiter frisch und trägt einen Hinweis.
          </p>
        </>
      ) : (
        <p className="q-job-text">
          {pruefliste
            ? `Prüfliste vom ${formatDatum(pruefliste.geprueft_am.slice(0, 10))}: ` +
              `${pruefliste.titel_geprueft} von ${pruefliste.titel_erwartet} Referenztiteln, ` +
              `${pruefliste.fehler} Abweichungen, ${pruefliste.hinweise} Hinweise ` +
              `(Geschäftsberichte vom ${formatDatum(String(pruefliste.referenzen_vom).slice(0, 10))}).`
            : 'Die Prüfliste ist noch nicht gelaufen.'}
        </p>
      )}

      {fehler.length > 0 && (
        <ul className="q-waechter-liste">
          {fehler.slice(0, 8).map((b, i) => (
            <li key={`${b.symbol}-${i}`}>
              <strong>{b.symbol}</strong> {b.text}
            </li>
          ))}
          {fehler.length > 8 && <li>… und {fehler.length - 8} weitere</li>}
        </ul>
      )}

      {gesperrt && (
        <div className="q-job-foot">
          <span className="q-job-meta">
            Freigeben rechnet die Listen neu — erst prüfen, woher die Abweichung kommt.
          </span>
          <button
            type="button"
            className="q-btn q-btn-primary q-btn-sm"
            disabled={freigeben.isPending}
            onClick={() => {
              if (window.confirm('Daten freigeben und Listen neu rechnen?')) freigeben.mutate()
            }}
          >
            {freigeben.isPending ? 'Gibt frei …' : 'Freigeben'}
          </button>
        </div>
      )}
    </section>
  )
}

function Katalog() {
  const jobs = useJobs()
  const starten = useTriggerJob()
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [ergebnis, setErgebnis] = useState<Record<string, { ok: boolean; text: string }>>({})

  const ausloesen = async (job: Job) => {
    if (!window.confirm(`„${job.titel}" jetzt starten?\n\nErwartete Dauer: rund ${job.dauer_minuten} Minuten.`)) return
    setLaeuft(job.schluessel)
    try {
      const antwort = await starten.mutateAsync(job.schluessel)
      setErgebnis((e) => ({ ...e, [job.schluessel]: { ok: true, text: `Gestartet. ${antwort.message}` } }))
    } catch (fehler) {
      setErgebnis((e) => ({ ...e, [job.schluessel]: { ok: false, text: fehlerText(fehler) } }))
    } finally {
      setLaeuft(null)
    }
  }

  if (jobs.isPending) {
    return (
      <div className="q-page">
        <section className="q-card">
          <Laden text="Lade den Job-Katalog …" />
        </section>
      </div>
    )
  }
  if (jobs.isError) {
    return (
      <div className="q-page">
        <section className="q-card">
          <Fehler fehler={jobs.error} nochmal={() => jobs.refetch()} />
        </section>
      </div>
    )
  }

  const { abo_aktiv: aboAktiv, jobs: liste } = jobs.data
  const gruppen = [
    { titel: 'Ohne Abo', eintraege: liste.filter((j) => !j.braucht_abo) },
    { titel: 'Mit Datenabo', eintraege: liste.filter((j) => j.braucht_abo) },
  ]

  return (
    <div className="q-page">
      <div className="q-placeholder-head">
        <span className={`q-badge ${aboAktiv ? 'q-badge--ok' : 'q-badge--warn'}`}>
          Datenabo {aboAktiv ? 'aktiv' : 'pausiert'}
        </span>
      </div>

      <Datenwaechter />
      <BestandKarte />

      {gruppen.map((gruppe) => (
        <section key={gruppe.titel} className="q-jobgroup">
          <h2 className="q-jobgroup-title">{gruppe.titel}</h2>
          <div className="q-jobs">
            {gruppe.eintraege.map((job) => {
              const r = ergebnis[job.schluessel]
              return (
                <article key={job.schluessel} className="q-card q-job">
                  <h3 className="q-job-title">{job.titel}</h3>
                  <p className="q-job-text">{job.zweck}</p>
                  {r && (
                    <div className={`q-job-result ${r.ok ? 'is-ok' : 'is-err'}`} role="status">
                      {r.text}
                    </div>
                  )}
                  <div className="q-job-foot">
                    <span className="q-job-meta">
                      ~{job.dauer_minuten} Min · {job.betriebsart}
                    </span>
                    <button
                      type="button"
                      className="q-btn q-btn-primary q-btn-sm"
                      disabled={!job.ausloesbar || laeuft !== null}
                      onClick={() => ausloesen(job)}
                      title={job.ausloesbar ? undefined : 'Braucht das Datenabo'}
                    >
                      {laeuft === job.schluessel ? 'Startet …' : 'Starten'}
                    </button>
                  </div>
                </article>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}
